import './FilterTreeHorizontal.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { useMountEffect, useUpdateEffect } from '@boost-sd/core-js';
import { LoadingIcon } from '@components/Icon/Loading';
import { avoidStickyHeader } from '@constants/selector';
import useTranslation from '@hooks/useTranslation';
import { useCurrency } from '@providers/CurrencyProvider';
import type { FilterOptionType, FilterParams } from '@providers/FilterProvider';
import {
  getFilterOptionByOptionId,
  hasScopeFilterParam,
  optionIdsOfRefineBy,
  useFilterSettings,
  useFilterState,
  useFilterTreeState,
} from '@providers/FilterProvider';
import type { stickyType } from '@utils';
import {
  createClsNameMap,
  getFilterAvoidStickyHeader,
  mergeModifiers,
  sticky,
  toItemArray,
} from '@utils';
import type { ModifiersType } from '@utils/classnames';
import classnames from '@utils/classnames';
import { useEffect, useRef } from 'react';

import { ApplyFilterOptionButton } from '@/features/filter-product-list/filter/ApplyFilterOptionButton';
import { clsNameMap as filterOptionCls } from '@/features/filter-product-list/filter/FilterOption';
import { FilterTreePlaceHolder } from '@/features/filter-product-list/filter/FilterTreePlaceholder';
import { ResetFilterOptionButton } from '@/features/filter-product-list/filter/ResetFilterOptionButton';

import RefineByHorizontal from '../../features/filter-product-list/filter/RefineByHorizontal';
import FilterOptionByType from '../FilterTree/FilterOptionByType';
import { clsNameMap as toolbarCls } from '../Toolbar';

const productListClassRoot = `.${classnames('product-list')}`;

export const clsNameMap = createClsNameMap({
  modifiers: ['hidden'],
  elements: {
    content: createClsNameMap({}),
    'option-list': createClsNameMap({
      modifiers: ['expand'],
    }),
    button: createClsNameMap({}),
    clear: createClsNameMap({
      modifiers: ['hidden'],
    }),
    'clear-all': createClsNameMap({}),
    'refine-by': createClsNameMap({}),
    'refine-by-title': createClsNameMap({}),
    'refine-by-heading': createClsNameMap({}),
    'refine-by-list': createClsNameMap({}),
    'refine-by-item': createClsNameMap({}),
    'refine-by-item-clear': createClsNameMap({}),
    'refine-by-type': createClsNameMap({}),
    'refine-by-option': createClsNameMap({}),
    'refine-by-value': createClsNameMap({}),
    'refine-by-clear': createClsNameMap({}),
  },
})('filter-tree-horizontal');

export type FilterTreeHorizontalProps = {
  modifiers?: ModifiersType<typeof clsNameMap>;
  customizeSticky?: (stickyData: stickyType) => void;
  filterOptionsShow?: FilterOptionType[];
};

const FilterTreeHorizontal = ({
  modifiers,
  customizeSticky,
  filterOptionsShow,
}: FilterTreeHorizontalProps) => {
  const { t } = useTranslation();
  const { rateCurrency, unitCurrency } = useCurrency();

  const {
    filterData,
    filterParams,
    ajaxLoading,
    removeFilterParams,
    handleChangeFilterParams,
    removeFilterParamsOption,
    setFilterOptionSelected,
    scopedFilterType,
  } = useFilterState();

  const {
    filterTreeHorizontalStyle,
    showRefineBy,
    showLoading,
    formatPipsRange,
    removePriceDecimal,
    stickyFilterOnDesktop,
    requestInstantly,
    filterAvoidStickyHeader,
    precisionFormatLabelSlider,
  } = useFilterSettings();

  const { filterTreeState, setFilterTreeVisible, refineByHorizontal, setRefineByHorizontal } =
    useFilterTreeState();

  // requestInstantly -> not show refine by immediate
  const _currentFilterParams = requestInstantly ? filterParams : refineByHorizontal;
  const getCurrentModifierType = () => {
    return filterTreeHorizontalStyle;
  };

  const isStickyFilterTree = ['style-expand'].includes(filterTreeHorizontalStyle);

  const filterTreeHorizontalElmRef = useRef<HTMLDivElement>(null);
  const filterTreeStickyContainer = useRef<HTMLDivElement>(document.createElement('div'));
  const isFiltering = !filterData || !filterData.data?.filter;

  // horizontal have only desktop style
  const stickyHorizontalDesktop = async () => {
    switch (filterTreeHorizontalStyle) {
      case 'style-expand':
        await sticky({
          stickyElement: document.getElementsByClassName(toolbarCls.container.m['style-expand']),
          endElement: productListClassRoot,
          avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
          isFullWidth: true,
          isMaxWidth: false,
          isToolbarIndex: true,
          callBack: (isSticky) => {
            isSticky
              ? document
                  .getElementsByClassName('boost-sd-right')[0]
                  .setAttribute(
                    'style',
                    `padding-top: ${
                      document.getElementsByClassName(toolbarCls.elm('container'))[0]?.clientHeight
                    }px`
                  )
              : document.getElementsByClassName('boost-sd-right')[0]?.removeAttribute('style');

            isSticky
              ? document
                  .getElementsByClassName(toolbarCls.elm('container'))[0]
                  .classList.add(classnames('toolbar-sticky'))
              : document
                  .getElementsByClassName(toolbarCls.elm('container'))[0]
                  .classList.remove(classnames('toolbar-sticky'));
          },
          isOpeningFilterTree: filterTreeState.filterTreeVisible,
          customizeSticky,
        });

        await sticky({
          stickyElement: document.getElementsByClassName(clsNameMap.root()),
          endElement: 'body',
          avoidElement: getFilterAvoidStickyHeader(
            filterAvoidStickyHeader,
            `${avoidStickyHeader}, .${toolbarCls.elm('container')}`
          ),
          isFullWidth: true,
          isMaxWidth: true,
          customStyle:
            'overflow-y: auto; overflow-x: hidden; border-top: 1px solid #ebebeb; border-bottom: 1px solid #ebebeb; box-shadow: 0 0 6px rgb(0 0 0 / 12%);',
          callBack: (isSticky) => {
            isSticky
              ? document.body.classList.add(classnames('filter-tree-horizontal-sticky-body'))
              : document.body.classList.remove(classnames('filter-tree-horizontal-sticky-body'));
          },
          customizeSticky,
        });

        await sticky({
          stickyElement: document.getElementsByClassName(clsNameMap.elm('content')),
          endElement: 'body',
          avoidElement: getFilterAvoidStickyHeader(
            filterAvoidStickyHeader,
            `${avoidStickyHeader}, .${toolbarCls.elm('container')}`
          ),
          customStyle: 'position: static;',
          isFullHeight: true, // full height,
          customizeSticky,
        });
        break;

      default:
        sticky({
          stickyElement: document.getElementsByClassName(clsNameMap.elm('content')),
          endElement: productListClassRoot,
          avoidElement: getFilterAvoidStickyHeader(filterAvoidStickyHeader, avoidStickyHeader),
          isFullWidth: true,
          isToolbarIndex: true,
          callBack: (isSticky) => {
            isSticky
              ? document.body.classList.add(classnames('filter-tree-horizontal-sticky-body'))
              : document.body.classList.remove(classnames('filter-tree-horizontal-sticky-body'));

            isSticky
              ? document
                  .getElementsByClassName('boost-sd-right')[0]
                  .setAttribute(
                    'style',
                    `padding-top: ${
                      document.getElementsByClassName(clsNameMap.elm('content'))[0]?.clientHeight
                    }px`
                  )
              : document.getElementsByClassName('boost-sd-right')[0]?.removeAttribute('style');
          },
          customizeSticky,
        });
        break;
    }
  };

  useMountEffect(() => {
    const documentClick = (e: Event) => {
      // click out size current itemwrapper or option title
      if (e.target) {
        const activeElement = e.target as HTMLElement;

        const clickItemWrapper = activeElement.closest(`.${filterOptionCls.elm('itemwrapper')}`);
        const clickEleInOptionTitle = activeElement.closest(`.${filterOptionCls.elm('title')}`);

        if (!clickItemWrapper && !clickEleInOptionTitle) {
          setFilterOptionSelected(null);
        }
      }
    };

    document.addEventListener('click', documentClick);
  });

  // use as component did mount or did update
  useEffect(() => {
    if (stickyFilterOnDesktop) stickyHorizontalDesktop();
  }, [isFiltering]);

  useUpdateEffect(() => {
    if (stickyFilterOnDesktop) {
      if (filterTreeState.filterTreeVisible) {
        document.body.classList.add(classnames('sticky-opening'));
      } else {
        document.body.classList.remove(classnames('sticky-opening'));
      }
    }
  }, [filterTreeState.filterTreeVisible]);

  if (isStickyFilterTree && !filterTreeStickyContainer.current.parentNode) {
    document.body.appendChild(filterTreeStickyContainer.current);
  }

  const renderRefineBy = ({
    hasButton = false,
    isRender = true,
  }: {
    hasButton?: boolean;
    isRender?: boolean;
  }) => {
    if (!isRender) return <></>;

    const refineByData: {
      filterOptionId: string;
      rawValue: string;
      label: string | undefined;
      value: string | undefined;
    }[] = [];

    optionIdsOfRefineBy(_currentFilterParams)?.forEach((filterOptionId) => {
      toItemArray(_currentFilterParams[filterOptionId])?.forEach((value: string) => {
        const { refineByLabel: _label, refineByValue: _value } = getFilterOptionByOptionId(
          filterData,
          filterOptionId,
          value,
          rateCurrency,
          unitCurrency,
          formatPipsRange,
          removePriceDecimal,
          { precisionFormatLabelSlider }
        );

        refineByData.push({
          filterOptionId,
          rawValue: value,
          label: _label,
          value: _value,
        });
      });
    });

    return (
      <RefineByHorizontal
        hasButton={hasButton}
        refineByData={refineByData}
        onRemove={(filterOptionId, value) => {
          removeFilterParams(filterOptionId, value, { forceInstantly: true });
          // remove item in _currentFilterParams
          if (_currentFilterParams[filterOptionId]?.length > 0) {
            _currentFilterParams[filterOptionId] = _currentFilterParams[filterOptionId]?.filter(
              (item: string) => item !== value
            );
          }
        }}
        onClearAll={() => {
          removeFilterParamsOption('', 'all');
          setRefineByHorizontal({});
        }}
      />
    );
  };

  const filterParamsClearAll = (_filterParams: FilterParams) => {
    return optionIdsOfRefineBy(_filterParams)?.length === 0;
  };

  const isStyleExpand = filterTreeHorizontalStyle === 'style-expand';
  const isLoadingIcon = filterData.loading || ajaxLoading;
  const isShowRefineByStyleExpand =
    showRefineBy && isStyleExpand && hasScopeFilterParam(filterData, filterParams);
  const isShowRefineByNotStyleExpand = showRefineBy && !isStyleExpand;

  const render = () => {
    return (
      <>
        <div className={clsNameMap.elm('content')}>
          <div
            className={mergeModifiers(clsNameMap['option-list'], [
              {
                expand: isStyleExpand,
              },
            ])}
          >
            {filterOptionsShow?.map((option) => {
              return (
                <FilterOptionByType
                  key={option.filterOptionId}
                  option={option}
                  currentModifierType={getCurrentModifierType()}
                />
              );
            })}
          </div>

          {isShowRefineByStyleExpand &&
            renderRefineBy({
              hasButton: false,
              isRender: !filterParamsClearAll(_currentFilterParams),
            })}

          {isStyleExpand && (
            <div className={clsNameMap.elm('button')}>
              <ApplyFilterOptionButton
                onClick={() => {
                  setRefineByHorizontal(filterParams);
                  handleChangeFilterParams(filterParams, { requestInstantlyHorizontal: true });
                  setFilterTreeVisible(false);
                }}
              >
                <span>{t('apply')}</span>
              </ApplyFilterOptionButton>

              {!filterParamsClearAll(_currentFilterParams) && (
                <ResetFilterOptionButton
                  style={{ padding: '10px 24px' }}
                  onClick={() => {
                    removeFilterParamsOption('', 'all');
                    setRefineByHorizontal({});
                  }}
                  aria-label={t('Clear all filters')}
                >
                  <span>{t('clearAll')}</span>
                </ResetFilterOptionButton>
              )}
            </div>
          )}
        </div>

        {isShowRefineByNotStyleExpand &&
          renderRefineBy({
            hasButton: true,
            isRender:
              !filterParamsClearAll(_currentFilterParams) &&
              hasScopeFilterParam(filterData, filterParams, scopedFilterType),
          })}
      </>
    );
  };

  return (
    <div
      className={mergeModifiers(clsNameMap, [
        modifiers,
        {
          hidden: isStyleExpand && !filterTreeState.filterTreeVisible,
        },
      ])}
      ref={filterTreeHorizontalElmRef}
    >
      {isFiltering ? <FilterTreePlaceHolder layout='horizontal' /> : render()}

      {showLoading && isLoadingIcon && <LoadingIcon modifiers={['fixed', 'has-shadow']} />}
    </div>
  );
};

export default registryComponent('FilterTreeHorizontal', FilterTreeHorizontal);
